import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/Safra/Icon";
import { useTranslation } from "contexts/Safra/TranslationContext";

import { initial_filter_hotels, isChannel, meal_plan } from "utils";
import {
    FieldInput,
    FilterOverlay,
    FilterSection,
    RadioInput,
    RadioLabel,
    SectionTitle,
    StarsCheckbox,
    StarsLine,
    StarsOption,
    StyledLabel,
    TwoColumnGrid
} from "components/Safra/FilterResult/styles";
import { trackHotelFilter } from "utils/analytics";
import { changeHotelNameAction } from "contexts/reducers/hotels/actions";
import { useApp } from "contexts/AppContext";
import { useGenialApp } from "contexts/GenialAppContext";
import { getSafraHotelsFilter, setSafraHotelsFilter } from "utils/safraHotelsSearchState";
import { ApplyButton, ClearButton, CloseButton, FilterActions, FilterContainer, FilterFooter, FilterHeading, SectionStack } from 'components/Safra/FilterAirfare/styles';

const starsCheckbox = ["5", "4", "3", "2", "1"];

const parseNumericValue = (value) => {
    const digitsOnly = String(value ?? "").replace(/\D/g, "");
    return digitsOnly ? Number(digitsOnly) : 0;
};

const sanitizeNumericInput = (value) => String(value ?? "").replace(/[^\d]/g, "").slice(0, 7);

const formatPointsInputValue = (value) => {
    const digits = sanitizeNumericInput(value);
    if (!digits) return "";
    return `${Number(digits).toLocaleString("pt-BR")} pts`;
};

const buildFilterPayload = (props) => {
    const {
        minValue,
        maxValue,
        stars,
        breakfast,
        offersHotel
    } = props;

    const defaultMin = initial_filter_hotels?.filterParameters?.price?.min ?? 0;
    const defaultMax = initial_filter_hotels?.filterParameters?.price?.max ?? 350000;
    const min = parseNumericValue(minValue);
    const normalizedMin = min > 0 ? min : defaultMin;

    const maxRaw = parseNumericValue(maxValue);
    const normalizedMax = maxRaw > 0 ? maxRaw : defaultMax;
    const max = normalizedMax >= normalizedMin ? normalizedMax : normalizedMin;

    return {
        filterParameters: {
            price: {
                min: normalizedMin,
                max,
                currency: offersHotel === "points" ? "POINTS" : "BRL"
            },
            hotelStars: stars,
            hotelBoards: [breakfast]
        }
    };
};

export default function HotelsFilter() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { hotelState, hotelDispatch } = !isChannel("genial") ? useApp() : useGenialApp();
    const { offersHotel, hotelNameSearch } = hotelState;

    const initialFilter = getSafraHotelsFilter();

    const [hotelName, setHotelName] = useState(hotelNameSearch?.name || "");
    const [priceMin, setPriceMin] = useState(String(initialFilter?.filterParameters?.price?.min ?? ""));
    const [priceMax, setPriceMax] = useState(String(initialFilter?.filterParameters?.price?.max ?? ""));
    const [isPriceMinFocused, setIsPriceMinFocused] = useState(false);
    const [isPriceMaxFocused, setIsPriceMaxFocused] = useState(false);
    const [stars, setStars] = useState(initialFilter?.filterParameters?.hotelStars || []);
    const [breakfast, setBreakfast] = useState(initialFilter?.filterParameters?.hotelBoards?.[0] ?? "breakfast");

    const mealLabel = useMemo(
        () => meal_plan.find((item) => item.value === breakfast)?.label || breakfast,
        [breakfast]
    );

    const handleToggleStars = (value, checked) => {
        if (checked) {
            setStars((state) => [...state, value].sort((a, b) => Number(a) - Number(b)));
            return;
        }

        setStars((state) => state.filter((item) => item !== value));
    };

    const handleApplyFilter = () => {
        const payload = buildFilterPayload({
            minValue: priceMin,
            maxValue: priceMax,
            stars,
            breakfast,
            offersHotel
        });

        setSafraHotelsFilter(payload);

        hotelDispatch(changeHotelNameAction({
            id: undefined,
            name: hotelName.trim()
        }));

        trackHotelFilter({
            points: payload.filterParameters.price.max,
            search_name: hotelName.trim(),
            stars: stars.join(","),
            refund: null,
            meal: mealLabel
        });

        navigate("/hotels-search-result");
    };

    const handleClearFilters = () => {
        setHotelName("");
        setPriceMin(String(initial_filter_hotels.filterParameters.price.min));
        setPriceMax(String(initial_filter_hotels.filterParameters.price.max));
        setStars(initial_filter_hotels.filterParameters.hotelStars);
        setBreakfast("breakfast");
    };

    const handleClose = () => {
        navigate("/hotels-search-result");
    };

    const renderStarsLine = (value) => {
        const filled = Number(value);
        return (
            <StarsLine>
                {Array.from({ length: filled }, (_, index) => (
                    <Icon key={`filled-${value}-${index}`} name="star" size={20} color="#00003C" />
                ))}
            </StarsLine>
        );
    };

    return (
        <FilterOverlay>
            <FilterContainer>
                <CloseButton type="button" onClick={handleClose} aria-label="Fechar filtros">
                    ×
                </CloseButton>
                <FilterHeading>{t('safra.hotel_search_result.filter_page.title')}</FilterHeading>
                <SectionStack>
                    <FilterSection>
                        <SectionTitle>{t('safra.hotel_search_result.filter_page.hotel_name')}</SectionTitle>
                        <FieldInput
                            type="text"
                            placeholder={t('safra.hotel_search_result.filter_page.placeholder_hotel')}
                            value={hotelName}
                            onChange={(e) => setHotelName(e.target.value)}
                            autoComplete="off"
                        />
                    </FilterSection>

                    <FilterSection>
                        <SectionTitle>{t('safra.hotel_search_result.filter_page.price_points')}</SectionTitle>
                        <TwoColumnGrid>
                            <FieldInput
                                type="text"
                                inputMode="numeric"
                                placeholder={t('safra.hotel_search_result.filter_page.min_price')}
                                value={isPriceMinFocused ? sanitizeNumericInput(priceMin) : formatPointsInputValue(priceMin)}
                                onChange={(e) => setPriceMin(sanitizeNumericInput(e.target.value))}
                                onFocus={() => setIsPriceMinFocused(true)}
                                onBlur={() => setIsPriceMinFocused(false)}
                            />
                            <FieldInput
                                type="text"
                                inputMode="numeric"
                                placeholder={t('safra.hotel_search_result.filter_page.max_price')}
                                value={isPriceMaxFocused ? sanitizeNumericInput(priceMax) : formatPointsInputValue(priceMax)}
                                onChange={(e) => setPriceMax(sanitizeNumericInput(e.target.value))}
                                onFocus={() => setIsPriceMaxFocused(true)}
                                onBlur={() => setIsPriceMaxFocused(false)}
                            />
                        </TwoColumnGrid>
                    </FilterSection>

                    <FilterSection>
                        <SectionTitle>{t('safra.hotel_search_result.filter_page.stars_title')}</SectionTitle>
                        <div>
                            {starsCheckbox.map((item) => {
                                const id = `hotel-stars-${item}`;
                                const checked = stars.includes(item);

                                return (
                                    <StarsOption key={item}>
                                        <StarsCheckbox
                                            id={id}
                                            type="checkbox"
                                            checked={checked}
                                            value={item}
                                            onChange={(e) => handleToggleStars(e.target.value, e.target.checked)}
                                        />
                                        <StyledLabel htmlFor={id}>
                                            {renderStarsLine(item)}
                                        </StyledLabel>
                                    </StarsOption>
                                );
                            })}
                        </div>
                    </FilterSection>
                </SectionStack>
                <FilterFooter>
                    <FilterActions>
                        <ClearButton type="button" onClick={handleClearFilters} aria-label="Limpar filtros selecionados">
                            {t('safra.hotel_search_result.filter_page.cta_clear')}
                        </ClearButton>
                        <ApplyButton type="button" onClick={handleApplyFilter} aria-label="Aplicar filtros selecionados">
                            {t('safra.hotel_search_result.filter_page.cta_apply')}
                        </ApplyButton>
                    </FilterActions>
                </FilterFooter>
            </FilterContainer>
        </FilterOverlay>
    );
}
